import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Globe, Sparkles, Wand2, Loader2, Save, 
    RefreshCw, AlertCircle, CheckCircle, ShieldCheck,
    ChevronRight, ExternalLink, Info, Activity,
    Target, BarChart3, Database, Eye, X, AlertTriangle,
    Award, Filter, Layout, Braces, Plus, Trash2,
    FileText, Bot, Rocket, Copy, Check, Terminal, Zap, BookOpen, Layers
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { PageSeoConfig, CertificationType } from '../../types';
import { useThemeTokens } from '../../hooks/useActiveTheme';

const COMMON_PATHS = [
    { path: '/', label: 'Gateway Page' },
    { path: '/faq', label: 'Primary FAQ Index' },
    { path: '/dashboard', label: 'Dashboard Main' },
    { path: '/guide', label: 'Theory Deck' },
    { path: '/skills', label: 'Skills Lab' },
    { path: '/simulator', label: 'Math Lab' },
    { path: '/exam', label: 'Simulation Mode' },
    { path: '/scenario', label: 'Case Studies' },
    { path: '/podcasts', label: 'Audio Library' },
    { path: '/search', label: 'AI Bridge' },
    { path: '/profile', label: 'Candidate ID' },
    { path: '/help', label: 'Instruction Manual' }
];

export const SeoManager: React.FC = () => {
    const tokens = useThemeTokens();
    const [activeTab, setActiveTab] = useState<'routes' | 'llm' | 'sitemap'>('routes');

    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [selectedCertId, setSelectedCertId] = useState<string>('global');
    const [configs, setConfigs] = useState<PageSeoConfig[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRebuildingSitemap, setIsRebuildingSitemap] = useState(false);
    const [isBoostingGuest, setIsBoostingGuest] = useState(false);
    const [sitemapInfo, setSitemapInfo] = useState<{ 
        success: boolean; 
        urlCount: number; 
        timestamp?: string;
        sitemapRootUrl?: string;
        llmRootUrl?: string;
    } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPage, setSelectedPage] = useState<Partial<PageSeoConfig> | null>(null);
    const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning', text: string} | null>(null);

    // FAQ Schema.org Automated Pipeline State
    const [faqPreviewSchema, setFaqPreviewSchema] = useState<string | null>(null);
    const [isGeneratingFaqSchema, setIsGeneratingFaqSchema] = useState(false);

    // Dynamic Route Injector Inputs
    const [customPathInput, setCustomPathInput] = useState('');
    const [customLabelInput, setCustomLabelInput] = useState('');

    // LLM Grounding State
    const [llmContent, setLlmContent] = useState<string>('');
    const [customLlmTemplate, setCustomLlmTemplate] = useState<string>('');
    const [isSavingLlmTemplate, setIsSavingLlmTemplate] = useState(false);
    const [isLoadingLlm, setIsLoadingLlm] = useState(false);
    const [llmTestQuery, setLlmTestQuery] = useState('Where can I take free CNA practice tests with rationales?');
    const [isTestingLlm, setIsTestingLlm] = useState(false);
    const [llmTestResult, setLlmTestResult] = useState<{
        query: string;
        answer: string;
        score: number;
        mentionsDiamond: boolean;
        urlMatches: string[];
        recommendations: string[];
        llmsTimestamp: string;
    } | null>(null);
    const [copiedLlm, setCopiedLlm] = useState(false);

    // Sitemap & Robots Preview State
    const [sitemapPreview, setSitemapPreview] = useState<{ xml: string; urlCount: number; certCount: number; customCount: number; timestamp: string } | null>(null);
    const [robotsPreview, setRobotsPreview] = useState<{ robotsTxt: string; aiBotsAllowed: string[]; timestamp: string } | null>(null);
    const [isLoadingAudit, setIsLoadingAudit] = useState(false);

    const loadData = async () => {
        setIsLoading(true);
        setStatus(null);
        try {
            const [c, data] = await Promise.all([
                StorageService.getCertifications(),
                StorageService.getAllSeoConfigs(selectedCertId)
            ]);
            setCerts(c);
            setConfigs(data);
        } catch (e) {
            console.error("SEO Registry sync failed.");
            setStatus({ type: 'error', text: 'Failed to synchronize SEO configurations.' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { 
        loadData(); 
    }, [selectedCertId]);

    // Load LLM Preview
    const loadLlmPreview = async () => {
        setIsLoadingLlm(true);
        try {
            const preview = await StorageService.getLlmPreview();
            setLlmContent(preview.content || '');
            setCustomLlmTemplate(preview.customTemplate || '');
        } catch (e) {
            console.error("Failed to load llms.txt preview", e);
        } finally {
            setIsLoadingLlm(false);
        }
    };

    // Load Sitemap & Robots Audit
    const loadSitemapAndRobots = async () => {
        setIsLoadingAudit(true);
        try {
            const [sm, rb] = await Promise.all([
                StorageService.getSitemapPreview(),
                StorageService.getRobotsPreview()
            ]);
            setSitemapPreview(sm);
            setRobotsPreview(rb);
        } catch (e) {
            console.error("Failed to load sitemap and robots previews", e);
        } finally {
            setIsLoadingAudit(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'llm') {
            loadLlmPreview();
        } else if (activeTab === 'sitemap') {
            loadSitemapAndRobots();
        }
    }, [activeTab]);

    const activeCert = certs.find(c => c.id === selectedCertId);

    const routeAudit = useMemo(() => {
        // Build base routes of COMMON_PATHS
        const baseAudits = COMMON_PATHS.map(route => {
            const config = configs.find(c => c.path === route.path);
            const score = config ? 
                (config.description?.length > 80 && config.title?.length > 15 && config.headerHierarchy?.h1 && config.jsonLd ? 100 : 60) : 0;
            
            return {
                ...route,
                config,
                score,
                status: score === 100 ? 'optimized' : (score > 0 ? 'warning' : 'critical')
            };
        });

        // Add any other user-defined routes present in configs that aren't in COMMON_PATHS
        const additionalAudits = configs
            .filter(c => !COMMON_PATHS.some(r => r.path === c.path))
            .map(c => {
                const score = c.description?.length > 80 && c.title?.length > 15 && c.headerHierarchy?.h1 && c.jsonLd ? 100 : 60;
                return {
                    path: c.path,
                    label: `Custom Path (${c.path})`,
                    config: c,
                    score,
                    status: score === 100 ? 'optimized' : 'warning'
                };
            });

        // Filter by search terms
        const merged = [...baseAudits, ...additionalAudits];
        if (!searchTerm) return merged;
        return merged.filter(item => 
            item.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
            item.path.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [configs, searchTerm]);

    const handleAddCustomRoute = () => {
        if (!customPathInput) return;
        let cleanPath = customPathInput.trim();
        if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
        
        // Prevent duplicate route inputs
        if (routeAudit.some(r => r.path === cleanPath)) {
            setStatus({ type: 'error', text: 'Path is already present in database directory.' });
            return;
        }

        setSelectedPage({
            path: cleanPath,
            title: customLabelInput ? `${customLabelInput} | Diamond Prep` : '',
            description: '',
            keywords: [],
            headerHierarchy: { h1: '', h2s: [] }, 
            jsonLd: ''
        });
        setCustomPathInput('');
        setCustomLabelInput('');
        setStatus({ type: 'success', text: `Pre-configured empty blueprint for ${cleanPath}.` });
    };

    const handleGenerateAiSuite = async () => {
        if (!selectedPage?.path) return;
        setIsGenerating(true);
        setStatus(null);
        try {
            const result = await StorageService.generateSeoSuite(
                selectedPage.path,
                selectedCertId
            );
            setSelectedPage(prev => ({
                ...prev,
                title: result.title || prev?.title || '',
                description: result.description || prev?.description || '',
                keywords: result.keywords || prev?.keywords || [],
                headerHierarchy: result.headerHierarchy || prev?.headerHierarchy || { h1: '', h2s: [] },
                jsonLd: result.jsonLd || prev?.jsonLd || ''
            }));
            setStatus({ type: 'success', text: 'AI generated full SEO & LLM ranking package successfully!' });
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'AI SEO Suite Generator failed. Please try again.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerateAiDescOnly = async () => {
        if (!selectedPage?.path) return;
        setIsGenerating(true);
        setStatus(null);
        try {
            const certName = activeCert?.name || 'General Medical Certification';
            const result = await StorageService.generateSeoDescription(
                selectedPage.path, 
                selectedPage.title || 'Clinical Hub',
                certName
            );
            setSelectedPage(prev => ({ ...prev, description: result.text }));
            setStatus({ type: 'success', text: 'AI generated niche-calibrated description.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'AI SEO engine offline.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSave = async () => {
        if (!selectedPage?.path) return;
        setIsSaving(true);
        try {
            await StorageService.saveSeoConfig({
                ...selectedPage,
                certId: selectedCertId
            });
            setStatus({ type: 'success', text: `Committed SEO configurations and header guidelines for ${selectedPage.path}.` });
            loadData();
            setTimeout(() => setSelectedPage(null), 1000);
        } catch (e) {
            setStatus({ type: 'error', text: 'Registry commit failed.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRebuildSitemap = async () => {
        setIsRebuildingSitemap(true);
        setStatus(null);
        try {
            const res = await StorageService.rebuildSitemap();
            setSitemapInfo({
                success: res.success,
                urlCount: res.urlCount,
                timestamp: res.timestamp || new Date().toLocaleTimeString(),
                sitemapRootUrl: res.sitemapRootUrl || 'https://study.diamondnursingschool.com/sitemap.xml',
                llmRootUrl: res.llmRootUrl || 'https://study.diamondnursingschool.com/llm.txt'
            });
            setStatus({ 
                type: 'success', 
                text: `Sitemap & LLM files generated on disk & live at root domain: https://study.diamondnursingschool.com/sitemap.xml and https://study.diamondnursingschool.com/llm.txt (${res.urlCount} active URLs).` 
            });
            if (activeTab === 'sitemap') {
                loadSitemapAndRobots();
            }
            if (activeTab === 'llm') {
                loadLlmPreview();
            }
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'Dynamic sitemap synchronizer failed to compile.' });
        } finally {
            setIsRebuildingSitemap(false);
        }
    };

    const handleBatchGuestBoost = async () => {
        setIsBoostingGuest(true);
        setStatus(null);
        try {
            const res = await StorageService.batchGuestBoost();
            setStatus({ 
                type: 'success', 
                text: `Guest SEO Boost complete! Optimized ${res.updatedCount} guest pathway routes with high-ranking metadata and JSON-LD schemas. Sitemap indexed at ${res.sitemapUrlCount} URLs.` 
            });
            loadData();
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'Batch guest boost operation failed.' });
        } finally {
            setIsBoostingGuest(false);
        }
    };

    const handleSaveLlmTemplate = async () => {
        setIsSavingLlmTemplate(true);
        setStatus(null);
        try {
            await StorageService.saveLlmTemplate(customLlmTemplate);
            setStatus({ type: 'success', text: 'Custom LLM instructions saved and llms.txt recompiled on disk!' });
            loadLlmPreview();
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'Failed to update custom LLM instructions.' });
        } finally {
            setIsSavingLlmTemplate(false);
        }
    };

    const handleRunLlmTest = async () => {
        if (!llmTestQuery.trim()) return;
        setIsTestingLlm(true);
        setLlmTestResult(null);
        setStatus(null);
        try {
            const result = await StorageService.testLlmSuggestion(llmTestQuery);
            setLlmTestResult(result);
            setStatus({ type: 'success', text: `Simulated LLM query tested. Citation Score: ${result.score}/100.` });
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'Failed to test LLM suggestion.' });
        } finally {
            setIsTestingLlm(false);
        }
    };

    const handlePreviewFaqSchema = async () => {
        setIsGeneratingFaqSchema(true);
        setStatus(null);
        try {
            const certId = selectedCertId || 'global';
            const url = certId !== 'global' 
                ? `/practice-exams/api/faqs?certId=${certId}` 
                : '/practice-exams/api/faqs';
            const res = await fetch(url);
            if (!res.ok) throw new Error("Could not sync FAQs catalog.");
            const data = await res.json();
            const items = data.items || [];
            
            const published = items.filter((f: any) => f.isPublished !== false);
            if (published.length === 0) {
                setStatus({ type: 'warning', text: 'No published FAQs found for this certification context.' });
                setFaqPreviewSchema(null);
                return;
            }

            const schemaObj = {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": published.map((f: any) => ({
                    "@type": "Question",
                    "name": f.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": f.answer
                    }
                }))
            };

            setFaqPreviewSchema(JSON.stringify(schemaObj, null, 2));
            setStatus({ type: 'success', text: `Compiled Schema.org FAQPage with ${published.length} active questions.` });
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: 'Failed to compile FAQ schema preview.' });
        } finally {
            setIsGeneratingFaqSchema(false);
        }
    };

    return (
        <div id="seo-commander-container" className="space-y-8 animate-fade-in pb-24 text-slate-900 max-w-7xl mx-auto px-4 md:px-0">
            
            {/* Hero Header */}
            <div id="seo-hero-header" className={`${tokens.heroBannerBg} p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/10`}>
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><Globe size={180}/></div>
                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-4">
                        <div className={`p-3 rounded-2xl shadow-xl ${tokens.iconBg}`}><Sparkles size={24}/></div>
                        <h2 className={`text-3xl font-black tracking-tight ${tokens.headerFont}`}>Advanced SEO & LLM Manager</h2>
                    </div>
                    <p className="text-slate-300 font-medium text-sm md:text-base max-w-3xl leading-relaxed">
                        Improve Google Search index rankings, rich snippets (FAQ, Course, Quiz schemas), and LLM search discovery across ChatGPT, Perplexity, and Google Gemini. Automatically syncs <span className="font-bold underline decoration-white/30">sitemap.xml</span>, <span className="font-bold underline decoration-white/30">llms.txt</span>, and dynamic SSR metadata on every configuration change.
                    </p>
                    
                    {/* Navigation Tabs */}
                    <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/15 pt-6">
                        <div className="bg-black/30 p-1.5 rounded-2xl border border-white/15 flex flex-wrap gap-1.5">
                            <button 
                                id="tab-btn-routes"
                                onClick={() => setActiveTab('routes')}
                                className={`px-5 py-2.5 rounded-xl text-xs font-black tracking-wide transition-all flex items-center gap-2 ${activeTab === 'routes' ? tokens.activeTab : 'text-slate-300 hover:text-white'}`}
                            >
                                <Layout size={14}/> Route Matrix & Meta Architect
                            </button>
                            <button 
                                id="tab-btn-llm"
                                onClick={() => setActiveTab('llm')}
                                className={`px-5 py-2.5 rounded-xl text-xs font-black tracking-wide transition-all flex items-center gap-2 ${activeTab === 'llm' ? tokens.activeTab : 'text-slate-300 hover:text-white'}`}
                            >
                                <Bot size={14}/> LLM Grounding & llms.txt
                            </button>
                            <button 
                                id="tab-btn-sitemap"
                                onClick={() => setActiveTab('sitemap')}
                                className={`px-5 py-2.5 rounded-xl text-xs font-black tracking-wide transition-all flex items-center gap-2 ${activeTab === 'sitemap' ? tokens.activeTab : 'text-slate-300 hover:text-white'}`}
                            >
                                <FileText size={14}/> Sitemap & Robots.txt Auditor
                            </button>
                        </div>

                        {/* Global Actions */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                id="btn-batch-boost-guest"
                                onClick={handleBatchGuestBoost}
                                disabled={isBoostingGuest}
                                className={`px-4 py-2.5 ${tokens.secondaryBtn} text-[11px] uppercase tracking-wider font-bold rounded-xl transition-all shadow-sm flex items-center gap-2 active:scale-95`}
                                title="Optimize all guest routes across certifications"
                            >
                                {isBoostingGuest ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
                                Boost All Guest Pages
                            </button>
                            <button
                                id="btn-rebuild-sitemap"
                                onClick={handleRebuildSitemap}
                                disabled={isRebuildingSitemap}
                                className={`px-4 py-2.5 ${tokens.primaryBtn} text-[11px] tracking-wider uppercase font-bold rounded-xl transition-all shadow-lg flex items-center gap-2 active:scale-95`}
                            >
                                {isRebuildingSitemap ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                Sync & Rebuild Files
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Notification alert */}
            {status && (
                <div id="seo-notification" className={`p-4 rounded-2xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : status.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertTriangle size={18}/> : status.type === 'warning' ? <AlertCircle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-bold text-xs tracking-wide">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl transition-all"><X size={16}/></button>
                </div>
            )}

            {/* Sitemap Info Box */}
            {sitemapInfo && (
                <div id="sitemap-status-box" className="p-6 bg-slate-900 text-white rounded-3xl border border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-pop">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-emerald-500/20 text-emerald-400 rounded-xl"><CheckCircle size={20}/></div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="text-xs font-black uppercase tracking-wider text-emerald-400">Sitemap XML & LLM Files Live at Root Domain</h4>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-950 text-emerald-300 border border-emerald-800">Root Published</span>
                            </div>
                            <p className="text-[11px] text-slate-300 mt-1 flex flex-wrap items-center gap-2">
                                <span>Canonical Root URLs:</span>
                                <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="underline text-emerald-300 hover:text-white font-mono font-bold">/sitemap.xml</a>
                                <span>•</span>
                                <a href="/llms.txt" target="_blank" rel="noreferrer" className="underline text-emerald-300 hover:text-white font-mono font-bold">/llms.txt (Canonical)</a>
                                <span>•</span>
                                <a href="/llms-full.txt" target="_blank" rel="noreferrer" className="underline text-slate-300 hover:text-white font-mono">/llms-full.txt</a>
                                <span>•</span>
                                <a href="/robots.txt" target="_blank" rel="noreferrer" className="underline text-slate-300 hover:text-white font-mono">/robots.txt</a>
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 text-right">
                        <div className="font-mono text-xs">
                            <div className="text-[10px] text-slate-500">Indexed URIs</div>
                            <div className="font-bold text-base text-white">{sitemapInfo.urlCount} nodes</div>
                        </div>
                        <div className="font-mono text-xs">
                            <div className="text-[10px] text-slate-500">Timestamp</div>
                            <div className="text-slate-300">{sitemapInfo.timestamp}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 1: Route Matrix & Meta Architect */}
            {activeTab === 'routes' && (
                <div className="space-y-6">
                    {/* Certification Filter Pills */}
                    <div className="flex items-center justify-between flex-wrap gap-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-1">Certification Scope:</span>
                            <button 
                                id="cert-btn-global"
                                onClick={() => setSelectedCertId('global')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${selectedCertId === 'global' ? tokens.activeTab : tokens.inactiveTab}`}
                            >
                                Global Defaults
                            </button>
                            {certs.map(c => (
                                <button 
                                    id={`cert-btn-${c.id}`}
                                    key={c.id}
                                    onClick={() => setSelectedCertId(c.id)}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${selectedCertId === c.id ? tokens.activeTab : tokens.inactiveTab}`}
                                >
                                    <Award size={13}/> {c.shortName}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`text-xs px-3 py-1.5 ${tokens.badge}`}>
                                Context: {selectedCertId.toUpperCase()}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
                        {/* Health Audit & Routes list */}
                        <div className="xl:col-span-7 space-y-6">
                            <div id="route-audit-card" className={`${tokens.cardBg} overflow-hidden flex flex-col shadow-sm`}>
                                <div className={`p-6 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 ${tokens.cardHeaderBg}`}>
                                    <div>
                                        <h3 className={`text-lg font-black tracking-tight flex items-center gap-2.5 ${tokens.headerFont}`}>
                                            <Activity size={20} className={tokens.accentText} /> Route Matrix: {selectedCertId.toUpperCase()}
                                        </h3>
                                        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">Registry discovery and SSR tag injection status</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <input 
                                                type="text" 
                                                placeholder="Search routes..."
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                                className="py-2 pl-8 pr-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500/20 transition-all w-44"
                                            />
                                            <Search size={13} className="absolute left-2.5 top-3 text-slate-400" />
                                        </div>
                                        <button onClick={loadData} className="p-2 text-slate-500 hover:text-blue-600 transition-all border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 shadow-xs" title="Refresh registry"><RefreshCw size={14}/></button>
                                    </div>
                                </div>
                                
                                <div className="overflow-y-auto max-h-[600px] custom-scrollbar flex-1">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-slate-50/50 dark:bg-slate-800/50 sticky top-0 z-10 border-b border-slate-100 dark:border-slate-800">
                                            <tr>
                                                <th className="py-3.5 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Routing Pathway</th>
                                                <th className="py-3.5 px-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">Score</th>
                                                <th className="py-3.5 px-6 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {routeAudit.map(item => (
                                                <tr key={item.path} className="group hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors">
                                                    <td className="py-3.5 px-6">
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-xs text-slate-900 dark:text-slate-100">{item.label}</span>
                                                                {item.config?.jsonLd && <span className="px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 rounded text-[9px] font-mono font-bold">Schema Valid</span>}
                                                            </div>
                                                            <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{selectedCertId !== 'global' ? `/${selectedCertId}${item.path}` : item.path}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3.5 px-4 text-center">
                                                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[10px] font-bold ${
                                                            item.status === 'optimized' ? 'bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400' :
                                                            item.status === 'warning' ? 'bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400' :
                                                            'bg-rose-50 dark:bg-rose-950/50 border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400'
                                                        }`}>
                                                            <div className={`w-1.5 h-1.5 rounded-full bg-current ${item.status === 'warning' || item.status === 'critical' ? 'animate-pulse' : ''}`}></div>
                                                            {item.status}
                                                        </div>
                                                    </td>
                                                    <td className="py-3.5 px-6 text-right">
                                                        <button 
                                                            id={`btn-configure-${item.path.replace('/', 'root')}`}
                                                            onClick={() => {
                                                                setFaqPreviewSchema(null);
                                                                setSelectedPage(item.config ? { autoFaqSchema: item.config.autoFaqSchema !== false, ...item.config } : { 
                                                                    path: item.path, 
                                                                    title: '', 
                                                                    description: '', 
                                                                    keywords: [], 
                                                                    headerHierarchy: { h1: '', h2s: [] }, 
                                                                    jsonLd: '',
                                                                    autoFaqSchema: true
                                                                });
                                                            }}
                                                            className={`px-4 py-2 ${tokens.primaryBtn} text-[10px] uppercase tracking-wider font-bold rounded-xl transition-all shadow-xs`}
                                                        >
                                                            Configure
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {routeAudit.length === 0 && (
                                                <tr>
                                                    <td colSpan={3} className="py-10 text-center text-slate-400 font-medium text-xs">No matching routes found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Quick Path Injector widget */}
                            <div id="seo-custom-route-injector" className={`${tokens.cardBg} p-6 shadow-sm space-y-4`}>
                                <div>
                                    <h4 className="text-xs font-bold tracking-wider uppercase text-slate-500">Custom Path Registry Injector</h4>
                                    <p className="text-[11px] text-slate-400 mt-0.5 font-medium">Add dynamic secondary pathways directly to your sitemap & SEO catalog</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Target Endpoint URL</label>
                                        <input 
                                            type="text" 
                                            placeholder="e.g. /faq/nnaap-clinical"
                                            value={customPathInput}
                                            onChange={e => setCustomPathInput(e.target.value)}
                                            className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Page Label</label>
                                        <div className="flex gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="e.g. NNAAP Clinical Procedures"
                                                value={customLabelInput}
                                                onChange={e => setCustomLabelInput(e.target.value)}
                                                className="flex-1 p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddCustomRoute}
                                                className={`px-4 py-2.5 ${tokens.primaryBtn} text-[10px] font-bold tracking-wider uppercase rounded-xl transition-all`}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Meta & Schema XML Content Builder */}
                        <div className="xl:col-span-5 flex flex-col gap-6">
                            {selectedPage ? (
                                <div id="meta-architect-sidebar" className={`${tokens.cardBg} shadow-xl overflow-hidden animate-slide-in-right flex flex-col`}>
                                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-950 text-white flex justify-between items-center shrink-0">
                                        <div>
                                            <h4 className="text-lg font-black tracking-tight flex items-center gap-2">
                                                <Target size={18} className="text-blue-400" /> Meta Architect
                                            </h4>
                                            <p className="text-[10px] font-medium text-slate-400 tracking-wider mt-0.5">Path: {selectedPage.path}</p>
                                        </div>
                                        <button onClick={() => setSelectedPage(null)} className="p-2 hover:bg-white/10 rounded-xl transition-all text-slate-300 hover:text-white"><X size={20}/></button>
                                    </div>

                                    <div className="p-6 space-y-6 flex-1 overflow-y-auto max-h-[750px] custom-scrollbar bg-slate-50/20">
                                        
                                        {/* AI Bulk Generator Button */}
                                        <div className="p-5 bg-gradient-to-r from-blue-900 to-indigo-950 border border-white/10 text-white rounded-2xl space-y-3 shadow-sm">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h5 className="text-[11px] font-bold tracking-wider uppercase text-blue-200">AI SEO Optimization Suite</h5>
                                                    <p className="text-[10px] text-slate-300 leading-relaxed max-w-xs mt-0.5">Generate metadata, structured schemas, and heading outlines calibrated for student search queries.</p>
                                                </div>
                                                <span className="text-[9px] font-bold bg-white/15 text-slate-200 px-2 py-0.5 rounded">Gemini 3.5</span>
                                            </div>
                                            <div className="flex gap-2 pt-1">
                                                <button 
                                                    id="btn-ai-generate-suite"
                                                    onClick={handleGenerateAiSuite}
                                                    disabled={isGenerating}
                                                    className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md active:scale-95"
                                                >
                                                    {isGenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12}/>}
                                                    Generate Full Suite
                                                </button>
                                                <button 
                                                    id="btn-ai-generate-desc"
                                                    onClick={handleGenerateAiDescOnly}
                                                    disabled={isGenerating}
                                                    className="px-3.5 py-2.5 bg-white/10 hover:bg-white/20 disabled:opacity-40 text-slate-200 font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all"
                                                    title="Generate Description Only"
                                                >
                                                    <Wand2 size={13} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">SEO Title Tag (&lt;title&gt;)</label>
                                            <input 
                                                id="input-title-tag"
                                                type="text" value={selectedPage.title || ''}
                                                onChange={e => setSelectedPage({...selectedPage, title: e.target.value})}
                                                className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-slate-800 dark:text-slate-100 text-xs outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                placeholder="e.g., CNA Practice Exams | Diamond School Of Nursing"
                                            />
                                            <div className="flex justify-between text-[9px] font-medium text-slate-400 px-1 mt-0.5">
                                                <span>Recommended: 50-60 characters</span>
                                                <span className={(selectedPage.title || '').length > 60 || (selectedPage.title || '').length < 20 ? 'text-amber-500 font-bold' : 'text-emerald-500 font-bold'}>
                                                    {(selectedPage.title || '').length} chars
                                                </span>
                                            </div>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">Meta Description</label>
                                            <textarea 
                                                id="textarea-meta-desc"
                                                value={selectedPage.description || ''}
                                                onChange={e => setSelectedPage({...selectedPage, description: e.target.value})}
                                                className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl font-medium text-xs text-slate-700 dark:text-slate-200 min-h-[90px] resize-none outline-none focus:ring-2 focus:ring-blue-500/20 transition-all leading-relaxed"
                                                placeholder="Enter high-conversion clinical description..."
                                            />
                                            <div className="flex justify-between text-[9px] font-medium text-slate-400 px-1">
                                                <span>Recommended: 120-160 characters</span>
                                                <span className={(selectedPage.description || '').length > 160 ? 'text-rose-500 font-bold' : 'text-emerald-500 font-bold'}>
                                                    {(selectedPage.description || '').length} / 160
                                                </span>
                                            </div>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">Meta Keywords</label>
                                            <input 
                                                id="input-keywords"
                                                type="text" 
                                                value={selectedPage.keywords ? selectedPage.keywords.join(', ') : ''}
                                                onChange={e => {
                                                    const list = e.target.value.split(',').map(k => k.trim()).filter(Boolean);
                                                    setSelectedPage({ ...selectedPage, keywords: list });
                                                }}
                                                className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-slate-800 dark:text-slate-100 text-xs outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                placeholder="e.g. cna, clinical skills, test prep, nclex"
                                            />
                                        </div>

                                        {/* Header tags tool */}
                                        <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl">
                                            <h5 className="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                                <Layout size={13} className={tokens.accentText}/> Header Hierarchy (H1 & H2 Tags)
                                            </h5>
                                            
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider ml-1">Primary Display H1 Header</label>
                                                <input 
                                                    id="input-h1-header"
                                                    type="text" 
                                                    value={selectedPage.headerHierarchy?.h1 || ''}
                                                    onChange={e => setSelectedPage({
                                                        ...selectedPage,
                                                        headerHierarchy: {
                                                            h1: e.target.value,
                                                            h2s: selectedPage.headerHierarchy?.h2s || []
                                                        }
                                                    })}
                                                    className="w-full p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-slate-800 dark:text-slate-100 text-xs outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                    placeholder="e.g. CNA Authorized Practice Exams"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center ml-1">
                                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Subtopic Anchors (H2)</label>
                                                    <button 
                                                        id="btn-add-h2"
                                                        type="button"
                                                        onClick={() => {
                                                            const currentH2s = selectedPage.headerHierarchy?.h2s || [];
                                                            setSelectedPage({
                                                                ...selectedPage,
                                                                headerHierarchy: {
                                                                    h1: selectedPage.headerHierarchy?.h1 || '',
                                                                    h2s: [...currentH2s, 'New Target Subheading']
                                                                }
                                                            });
                                                        }}
                                                        className="text-[9px] font-bold text-blue-600 dark:text-blue-400 hover:underline px-2 py-1 bg-blue-50 dark:bg-blue-950/50 rounded-lg transition-all flex items-center gap-1"
                                                    >
                                                        <Plus size={10} /> Add H2
                                                    </button>
                                                </div>
                                                
                                                <div className="space-y-1.5">
                                                    {(selectedPage.headerHierarchy?.h2s || []).map((h2, idx) => (
                                                        <div key={idx} className="flex items-center gap-1.5">
                                                            <span className="text-[9px] font-mono text-slate-400 font-bold w-6">H2.{idx+1}</span>
                                                            <input 
                                                                id={`input-h2-${idx}`}
                                                                type="text" 
                                                                value={h2}
                                                                onChange={e => {
                                                                    const copyH2s = [...(selectedPage.headerHierarchy?.h2s || [])];
                                                                    copyH2s[idx] = e.target.value;
                                                                    setSelectedPage({
                                                                        ...selectedPage,
                                                                        headerHierarchy: {
                                                                            h1: selectedPage.headerHierarchy?.h1 || '',
                                                                            h2s: copyH2s
                                                                        }
                                                                    });
                                                                }}
                                                                className="flex-1 p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg font-medium text-slate-700 dark:text-slate-200 text-xs outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                            />
                                                            <button 
                                                                id={`btn-delete-h2-${idx}`}
                                                                type="button"
                                                                onClick={() => {
                                                                    const filteredH2s = (selectedPage.headerHierarchy?.h2s || []).filter((_, i) => i !== idx);
                                                                    setSelectedPage({
                                                                        ...selectedPage,
                                                                        headerHierarchy: {
                                                                            h1: selectedPage.headerHierarchy?.h1 || '',
                                                                            h2s: filteredH2s
                                                                        }
                                                                    });
                                                                }}
                                                                className="p-2 bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-100 transition-colors shrink-0"
                                                            >
                                                                <Trash2 size={12}/>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* FAQ Schema.org Automated Pipeline */}
                                        {selectedPage.path?.includes('/faq') && (
                                            <div id="faq-schema-pipeline-box" className="p-4 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-2xl space-y-3">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex items-center gap-2">
                                                        <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-lg">
                                                            <Braces size={14} />
                                                        </div>
                                                        <div>
                                                            <h5 className="text-[10px] font-bold tracking-wider uppercase text-indigo-950 dark:text-indigo-200">FAQ Schema Pipeline</h5>
                                                            <p className="text-[9px] text-slate-500 dark:text-slate-400 font-medium">Injects Schema.org FAQPage for Google Rich Snippets</p>
                                                        </div>
                                                    </div>
                                                    <span className="text-[8px] bg-indigo-600 text-white px-2 py-0.5 rounded font-bold uppercase tracking-wider">Dynamic SSR</span>
                                                </div>

                                                <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-indigo-100 dark:border-indigo-900/30 rounded-xl">
                                                    <div className="space-y-0.5 max-w-[70%]">
                                                        <p className="text-[10px] font-bold text-slate-800 dark:text-slate-100">Auto-Generate & Attach FAQ Schema</p>
                                                        <p className="text-[8px] text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                                                            Pulls published FAQs for {selectedCertId.toUpperCase()} and embeds JSON-LD blocks in SSR page markup.
                                                        </p>
                                                    </div>
                                                    <button
                                                        id="toggle-auto-faq-schema"
                                                        type="button"
                                                        onClick={() => setSelectedPage(prev => ({
                                                            ...prev,
                                                            autoFaqSchema: !prev?.autoFaqSchema
                                                        }))}
                                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${selectedPage?.autoFaqSchema ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                                                    >
                                                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${selectedPage?.autoFaqSchema ? 'translate-x-5' : 'translate-x-0'}`} />
                                                    </button>
                                                </div>

                                                <button
                                                    id="btn-preview-faq-schema"
                                                    type="button"
                                                    onClick={handlePreviewFaqSchema}
                                                    disabled={isGeneratingFaqSchema}
                                                    className="w-full py-2 bg-slate-900 dark:bg-slate-800 text-white hover:bg-slate-800 disabled:opacity-40 text-[9px] font-bold tracking-wider uppercase rounded-xl transition-all flex items-center justify-center gap-1.5"
                                                >
                                                    {isGeneratingFaqSchema ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11}/>}
                                                    Preview FAQ Schema Block
                                                </button>

                                                {faqPreviewSchema && (
                                                    <div id="faq-schema-preview-area" className="space-y-1.5 animate-fade-in">
                                                        <div className="flex justify-between items-center px-1">
                                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">JSON-LD Preview</span>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => setFaqPreviewSchema(null)}
                                                                className="text-[9px] font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline"
                                                            >
                                                                Close
                                                            </button>
                                                        </div>
                                                        <pre className="p-3 bg-slate-950 text-emerald-400 font-mono text-[9px] rounded-xl overflow-x-auto max-h-[150px] custom-scrollbar">
                                                            {faqPreviewSchema}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Schema JSON data */}
                                        <div className="space-y-1.5 border border-slate-200 dark:border-slate-700 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                                    <Braces size={12}/> Custom Structured Schema (JSON-LD)
                                                </label>
                                                <div className="text-[8px] bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded font-mono font-bold uppercase">application/ld+json</div>
                                            </div>
                                            <textarea 
                                                id="textarea-json-ld"
                                                value={selectedPage.jsonLd || ''}
                                                onChange={e => setSelectedPage({ ...selectedPage, jsonLd: e.target.value })}
                                                className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl font-mono text-[10px] leading-relaxed text-emerald-400 min-h-[120px] resize-y outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all shadow-inner"
                                                placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "Course",\n  "name": "CNA Practice Exams",\n  "description": "Authorized clinical prep"\n}`}
                                            />
                                        </div>

                                        {/* Substitution keys */}
                                        <div className="p-4 bg-slate-900 text-white rounded-2xl space-y-2.5">
                                            <h5 className="text-[9px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                                                <Braces size={11}/> Dynamic Replacement Tags
                                            </h5>
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { key: '{{certName}}', val: activeCert?.name || 'CNA' },
                                                    { key: '{{certShortName}}', val: activeCert?.shortName || 'CNA' },
                                                    { key: '{{state}}', val: 'Global' }
                                                ].map(v => (
                                                    <div key={v.key} className="p-2 bg-white/5 border border-white/10 rounded-lg">
                                                        <p className="text-[9px] font-mono text-white font-bold">{v.key}</p>
                                                        <p className="text-[8px] text-slate-400 truncate mt-0.5">{v.val}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 flex justify-end gap-3 shadow-md z-10">
                                        <button id="btn-meta-discard" onClick={() => setSelectedPage(null)} className={`px-4 py-2.5 ${tokens.secondaryBtn} font-bold text-[10px] uppercase tracking-wider`}>Discard</button>
                                        <button 
                                            id="btn-meta-save"
                                            onClick={handleSave}
                                            disabled={isSaving}
                                            className={`px-6 py-2.5 ${tokens.primaryBtn} font-bold text-[10px] uppercase tracking-wider flex items-center gap-2`}
                                        >
                                            {isSaving ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13}/>} Commit Changes
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div id="meta-architect-empty" className="bg-slate-900 rounded-[2.5rem] p-10 text-white text-center space-y-6 shadow-xl relative overflow-hidden group h-[500px] flex flex-col items-center justify-center border border-white/10">
                                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:rotate-12 transition-transform duration-1000"><Database size={140}/></div>
                                    <div className="w-20 h-20 bg-white/5 border border-white/15 rounded-3xl flex items-center justify-center mx-auto shadow-2xl">
                                        <Search size={36} className="text-blue-400" />
                                    </div>
                                    <div className="max-w-xs space-y-2">
                                        <h4 className="text-lg font-black tracking-tight leading-snug text-white">Select a Routing Pathway</h4>
                                        <p className="text-slate-400 text-xs font-medium leading-relaxed">
                                            Click <span className="text-white font-bold">Configure</span> on any pathway in the Route Matrix to edit titles, rich descriptions, JSON-LD schemas, and heading outlines.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: LLM Grounding & llms.txt Manager */}
            {activeTab === 'llm' && (
                <div className="space-y-8 animate-fade-in">
                    
                    {/* Header bar with direct links */}
                    <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-2xl ${tokens.iconBg}`}><Bot size={22}/></div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className={`text-xl font-black ${tokens.headerFont}`}>LLM Context & llms.txt Grounding Hub</h3>
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">Root Domain Live</span>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    Canonical manifest live at <span className="font-mono font-bold text-slate-800 dark:text-slate-200">https://study.diamondnursingschool.com/llms.txt</span> for ChatGPT, Gemini, Claude, and Perplexity
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            <a 
                                href="/llms.txt" 
                                target="_blank" 
                                rel="noreferrer"
                                className={`px-4 py-2.5 ${tokens.primaryBtn} text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-sm`}
                                title="Root Domain: https://study.diamondnursingschool.com/llms.txt"
                            >
                                <ExternalLink size={13}/> View Live /llms.txt (Root)
                            </a>
                            <a 
                                href="/llms-full.txt" 
                                target="_blank" 
                                rel="noreferrer"
                                className={`px-4 py-2.5 ${tokens.secondaryBtn} text-xs font-bold rounded-xl flex items-center gap-1.5`}
                                title="Standard RFC: https://study.diamondnursingschool.com/llms-full.txt"
                            >
                                <ExternalLink size={13}/> View /llms-full.txt (Extended)
                            </a>
                            <a 
                                href="/sitemap.xml" 
                                target="_blank" 
                                rel="noreferrer"
                                className={`px-3 py-2.5 ${tokens.secondaryBtn} text-xs font-bold rounded-xl flex items-center gap-1.5`}
                            >
                                <ExternalLink size={13}/> /sitemap.xml
                            </a>
                            <button
                                onClick={handleRebuildSitemap}
                                disabled={isRebuildingSitemap}
                                className={`px-4 py-2.5 ${tokens.secondaryBtn} text-xs font-bold rounded-xl flex items-center gap-1.5`}
                            >
                                {isRebuildingSitemap ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
                                Recompile Files
                            </button>
                        </div>
                    </div>

                    {/* Root Domain Verification Card */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800/60 p-4 rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-emerald-900 dark:text-emerald-200">
                        <div className="flex items-center gap-2.5">
                            <CheckCircle size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0"/>
                            <div>
                                <span className="font-bold">Root Domain Manifest Active: </span>
                                AI crawlers can fetch the manifest directly at <code className="bg-white/80 dark:bg-slate-900 px-1.5 py-0.5 rounded font-mono font-bold text-emerald-800 dark:text-emerald-300">https://study.diamondnursingschool.com/llms.txt</code>.
                                <span className="text-slate-600 dark:text-slate-400 ml-1">Changes to programs, certifications, or custom templates automatically synchronize to disk and live endpoints. Legacy paths like <code className="font-mono">/practice-exam/llms.txt</code> permanently redirect (301) to root.</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left column: Live llms.txt preview */}
                        <div className="lg:col-span-6 space-y-4">
                            <div className={`${tokens.cardBg} p-6 shadow-sm space-y-4 flex flex-col h-[700px]`}>
                                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                                    <div className="flex items-center gap-2">
                                        <FileText size={16} className={tokens.accentText}/>
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">Compiled llms.txt Manifest</h4>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(llmContent);
                                                setCopiedLlm(true);
                                                setTimeout(() => setCopiedLlm(false), 2000);
                                            }}
                                            className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5"
                                        >
                                            {copiedLlm ? <Check size={12} className="text-emerald-500"/> : <Copy size={12}/>}
                                            {copiedLlm ? 'Copied' : 'Copy'}
                                        </button>
                                        <button
                                            onClick={loadLlmPreview}
                                            disabled={isLoadingLlm}
                                            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-all rounded-lg"
                                            title="Reload"
                                        >
                                            <RefreshCw size={14} className={isLoadingLlm ? 'animate-spin' : ''}/>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto bg-slate-950 p-4 rounded-2xl font-mono text-[11px] leading-relaxed text-emerald-300 custom-scrollbar shadow-inner">
                                    {isLoadingLlm ? (
                                        <div className="flex items-center justify-center h-full text-slate-500 gap-2">
                                            <Loader2 size={16} className="animate-spin"/> Loading compiled manifest...
                                        </div>
                                    ) : (
                                        <pre className="whitespace-pre-wrap">{llmContent || 'No llms.txt compiled yet. Click Recompile.'}</pre>
                                    )}
                                </div>
                            </div>

                            {/* Custom Institutional Guidance Editor */}
                            <div className={`${tokens.cardBg} p-6 shadow-sm space-y-4`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                            <Terminal size={15} className={tokens.accentText}/> Custom Institutional Guidance
                                        </h4>
                                        <p className="text-[11px] text-slate-400 mt-0.5">Instructions added here are injected directly into llms.txt to guide LLM recommendations</p>
                                    </div>
                                    <button
                                        onClick={handleSaveLlmTemplate}
                                        disabled={isSavingLlmTemplate}
                                        className={`px-4 py-2 ${tokens.primaryBtn} text-xs font-bold rounded-xl flex items-center gap-1.5`}
                                    >
                                        {isSavingLlmTemplate ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>}
                                        Save Guidance
                                    </button>
                                </div>

                                <textarea
                                    value={customLlmTemplate}
                                    onChange={e => setCustomLlmTemplate(e.target.value)}
                                    placeholder="Enter custom prompt guidance, key differentiators, accreditation facts, and citation priorities for LLMs..."
                                    className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-medium text-slate-800 dark:text-slate-100 min-h-[140px] resize-y outline-none focus:ring-2 focus:ring-blue-500/20"
                                />
                            </div>
                        </div>

                        {/* Right column: Interactive LLM Suggestion & Citation Tester */}
                        <div className="lg:col-span-6 space-y-6">
                            <div className={`${tokens.cardBg} p-6 shadow-sm space-y-6`}>
                                <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <div className="flex items-center gap-2">
                                        <Zap size={18} className={tokens.accentText}/>
                                        <h4 className={`text-base font-black ${tokens.headerFont}`}>LLM Suggestion & Citation Simulator</h4>
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        Simulate how an AI assistant (like Gemini or ChatGPT) answers student search queries using the active <span className="font-mono font-bold text-slate-700 dark:text-slate-300">llms.txt</span> manifest.
                                    </p>
                                </div>

                                {/* Sample queries */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Example Inquiries to Test</label>
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            'Where can I find free CNA practice tests with rationales?',
                                            'How do I study for phlebotomy certification?',
                                            'Where can I practice medical assistant clinical skills?',
                                            'Does Diamond School offer timed exam simulations?'
                                        ].map((q, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setLlmTestQuery(q)}
                                                className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 text-[11px] font-medium rounded-xl transition-all border border-slate-200 dark:border-slate-700 text-left"
                                            >
                                                "{q}"
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Input query */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Student Search Query</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={llmTestQuery}
                                            onChange={e => setLlmTestQuery(e.target.value)}
                                            placeholder="Enter student question..."
                                            className="flex-1 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-500/20"
                                        />
                                        <button
                                            id="btn-run-llm-test"
                                            onClick={handleRunLlmTest}
                                            disabled={isTestingLlm || !llmTestQuery.trim()}
                                            className={`px-5 py-3 ${tokens.primaryBtn} text-xs font-bold rounded-xl flex items-center gap-2 shrink-0`}
                                        >
                                            {isTestingLlm ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
                                            Run Test
                                        </button>
                                    </div>
                                </div>

                                {/* Results display */}
                                {llmTestResult && (
                                    <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800 animate-fade-in">
                                        {/* Scorecard */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                                                <div className="text-[10px] text-slate-400 uppercase font-bold">Citation Score</div>
                                                <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-0.5">{llmTestResult.score}<span className="text-xs text-slate-400">/100</span></div>
                                            </div>

                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
                                                <div className="text-[10px] text-slate-400 uppercase font-bold">Brand Mention</div>
                                                <div className={`text-xs font-black mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${llmTestResult.mentionsDiamond ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'}`}>
                                                    {llmTestResult.mentionsDiamond ? <CheckCircle size={12}/> : <AlertTriangle size={12}/>}
                                                    {llmTestResult.mentionsDiamond ? 'Cited Diamond' : 'Mention Omitted'}
                                                </div>
                                            </div>

                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center col-span-2 sm:col-span-1">
                                                <div className="text-[10px] text-slate-400 uppercase font-bold">Matched Links</div>
                                                <div className="text-xl font-black text-slate-800 dark:text-slate-100 mt-1">{llmTestResult.urlMatches?.length || 0}</div>
                                            </div>
                                        </div>

                                        {/* Generated Response */}
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Simulated AI Assistant Output</label>
                                            <div className="p-4 bg-slate-900 text-slate-200 rounded-2xl text-xs leading-relaxed max-h-[220px] overflow-y-auto custom-scrollbar">
                                                {llmTestResult.answer}
                                            </div>
                                        </div>

                                        {/* Extracted URLs */}
                                        {llmTestResult.urlMatches?.length > 0 && (
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Referenced URLs</label>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {llmTestResult.urlMatches.map((u, i) => (
                                                        <span key={i} className="text-[10px] font-mono px-2 py-1 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md">
                                                            {u}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Strategic Suggestions */}
                                        {llmTestResult.recommendations?.length > 0 && (
                                            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-2xl space-y-2">
                                                <h5 className="text-[11px] font-bold uppercase tracking-wider text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                                                    <Sparkles size={13}/> LLM Discoverability Recommendations
                                                </h5>
                                                <ul className="space-y-1.5">
                                                    {llmTestResult.recommendations.map((rec, i) => (
                                                        <li key={i} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                                                            <span>{rec}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* TAB 3: Sitemap & Robots.txt Auditor */}
            {activeTab === 'sitemap' && (
                <div className="space-y-8 animate-fade-in">
                    
                    {/* Header stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className={`${tokens.cardBg} p-5 shadow-sm space-y-1`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Indexed URLs</div>
                            <div className="text-3xl font-black text-slate-900 dark:text-slate-100">{sitemapPreview?.urlCount || 0}</div>
                            <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">Valid nodes in sitemap.xml</div>
                        </div>

                        <div className={`${tokens.cardBg} p-5 shadow-sm space-y-1`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Active Certifications</div>
                            <div className="text-3xl font-black text-slate-900 dark:text-slate-100">{sitemapPreview?.certCount || 0}</div>
                            <div className="text-[11px] text-slate-500 font-medium">Program specific paths</div>
                        </div>

                        <div className={`${tokens.cardBg} p-5 shadow-sm space-y-1`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Custom Pathway Nodes</div>
                            <div className="text-3xl font-black text-slate-900 dark:text-slate-100">{sitemapPreview?.customCount || 0}</div>
                            <div className="text-[11px] text-slate-500 font-medium">Admin injected routes</div>
                        </div>

                        <div className={`${tokens.cardBg} p-5 shadow-sm space-y-1`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Last Rebuild</div>
                            <div className="text-sm font-black text-slate-800 dark:text-slate-200 mt-2 truncate">{sitemapPreview?.timestamp || 'Just now'}</div>
                            <div className="text-[11px] text-blue-600 dark:text-blue-400 font-medium">Sync with disk completed</div>
                        </div>
                    </div>

                    {/* Inspection section */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Sitemap XML Viewer */}
                        <div className="lg:col-span-8 space-y-4">
                            <div className={`${tokens.cardBg} p-6 shadow-sm space-y-4 flex flex-col h-[650px]`}>
                                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3 flex-wrap gap-2">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                                <FileText size={15} className={tokens.accentText}/> Live Sitemap.xml File
                                            </h4>
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">Root Domain</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            Canonical location: <span className="font-mono font-bold text-slate-700 dark:text-slate-200">https://study.diamondnursingschool.com/sitemap.xml</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <a
                                            href="/sitemap.xml"
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`px-3.5 py-1.5 ${tokens.primaryBtn} text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-sm`}
                                            title="Root Domain: https://study.diamondnursingschool.com/sitemap.xml"
                                        >
                                            <ExternalLink size={12}/> Open /sitemap.xml (Root)
                                        </a>
                                        <button
                                            onClick={loadSitemapAndRobots}
                                            disabled={isLoadingAudit}
                                            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-all rounded-lg"
                                            title="Reload"
                                        >
                                            <RefreshCw size={14} className={isLoadingAudit ? 'animate-spin' : ''}/>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto bg-slate-950 p-4 rounded-2xl font-mono text-[11px] leading-relaxed text-blue-300 custom-scrollbar shadow-inner">
                                    {isLoadingAudit ? (
                                        <div className="flex items-center justify-center h-full text-slate-500 gap-2">
                                            <Loader2 size={16} className="animate-spin"/> Loading sitemap XML...
                                        </div>
                                    ) : (
                                        <pre className="whitespace-pre-wrap">{sitemapPreview?.xml || 'No sitemap content generated yet.'}</pre>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Robots.txt & Bot Directives Viewer */}
                        <div className="lg:col-span-4 space-y-6">
                            <div className={`${tokens.cardBg} p-6 shadow-sm space-y-4`}>
                                <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                        <Bot size={15} className={tokens.accentText}/> Robots.txt Directives
                                    </h4>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Rules for search bots and AI scrapers</p>
                                </div>

                                <div className="p-3 bg-slate-950 rounded-xl font-mono text-[10px] text-slate-300 max-h-[220px] overflow-y-auto custom-scrollbar">
                                    <pre className="whitespace-pre-wrap">{robotsPreview?.robotsTxt || 'User-agent: *\nAllow: /\nSitemap: https://study.diamondnursingschool.com/sitemap.xml'}</pre>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">AI Crawlers Explicitly Allowed</label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {['Googlebot', 'GPTBot', 'ClaudeBot', 'PerplexityBot', 'CCBot', 'Google-Extended'].map((bot, i) => (
                                            <span key={i} className="px-2 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-[10px] font-bold rounded-md flex items-center gap-1">
                                                <Check size={10} className="text-emerald-500"/> {bot}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <a
                                        href="/robots.txt"
                                        target="_blank"
                                        rel="noreferrer"
                                        className={`w-full py-2.5 ${tokens.secondaryBtn} text-xs font-bold rounded-xl flex items-center justify-center gap-1.5`}
                                    >
                                        <ExternalLink size={13}/> View Live /robots.txt
                                    </a>
                                </div>
                            </div>

                            {/* Verification Directives */}
                            <div className={`${tokens.cardBg} p-6 shadow-sm space-y-4`}>
                                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Info size={14} className={tokens.accentText}/> Search Engine Best Practices
                                </h4>
                                <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                        <span>Clean path matching: All certification URLs index cleanly without hash fragments for crawlers.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                                        <span>Dynamic SSR Tag Injection: Server injects title, description, and JSON-LD schema into index.html before returning to crawlers.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0"></div>
                                        <span>Instant Cache Busting: Saving metadata automatically regenerates disk sitemap and LLM files.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};
